/**
 * DRBFM Analysis Page
 *
 * Main page for DRBFM (Design Review Based on Failure Mode) analysis.
 * Implements a wizard-based workflow for design change analysis.
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  CircularProgress,
  Button,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '@store/index';
import { useDRBFM } from '@hooks/useDRBFM';
import { selectCadEnabled } from '@features/featureFlags/featureFlagsSlice';
import {
  setCurrentSession,
  setAnalysisResults,
  setWizardStep,
  setAnalysisError,
} from '@features/drbfm/drbfmSlice';
import { drbfmService } from '@services/drbfm.service';
import DRBFMInputStep from '@components/drbfm/steps/DRBFMInputStep';
import DRBFMProcessingStep from '@components/drbfm/steps/DRBFMProcessingStep';
import DRBFMResultsStep from '@components/drbfm/steps/DRBFMResultsStep';
import DRBFMCaseWorkflow from '@components/drbfm/case/DRBFMCaseWorkflow';
import type { PredefinedPartLabel } from '../types';

interface WizardStepConfig {
  id: number;
  key: 'input' | 'processing' | 'results';
  labelKey: string;
}

const DRBFMPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const cadEnabled = useAppSelector(selectCadEnabled);
  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session');
  const modeParam = searchParams.get('mode');
  const isCaseMode = modeParam === 'case';
  const [isLinkingCad, setIsLinkingCad] = React.useState(false);
  const [linkCadError, setLinkCadError] = React.useState<string | null>(null);
  const {
    wizardStep,
    inputData,
    analysisProgress,
    analysisResults,
    isLoading,
    error,
    canStartAnalysis,
    isExporting,
    exportError,
    createSessionAndAnalyze,
    updateInput,
    goToStep,
    reset,
    exportHTML,
    cancelAnalysis,
  } = useDRBFM();

  // When arriving from the history list via `/drbfm?session=<id>`, hydrate
  // the wizard from that session: load metadata + results, then jump to the
  // results step (DRBFMResultsStep). Without this the page mounts on Step 1
  // with an empty form and the "結果を表示" action looked broken (TG #673).
  //
  // The loader is inlined here (instead of useDRBFM.loadSession) so we can
  // guard each Redux dispatch against the URL changing mid-fetch. The ref
  // tracks the currently-targeted session id; any await whose surrounding
  // request id no longer matches the ref bails before touching Redux.
  const sessionAutoLoadRef = useRef<string | null>(null);
  useEffect(() => {
    if (isCaseMode) {
      sessionAutoLoadRef.current = null;
      return;
    }

    if (!sessionIdParam) {
      // Bare /drbfm always returns to a clean slate. The earlier
      // ref-only check failed on remount (e.g. /drbfm/history → /drbfm
      // re-mounts the component with ref=null), so we now reset
      // unconditionally.
      sessionAutoLoadRef.current = null;
      reset();
      return;
    }
    if (sessionAutoLoadRef.current === sessionIdParam) return;
    sessionAutoLoadRef.current = sessionIdParam;
    const requested = sessionIdParam;
    // Clear any previously-loaded session before kicking off the new
    // request. Without this, a quick switch from /drbfm?session=B to
    // /drbfm?session=A briefly renders B's results, export, and 3D
    // buttons under A's URL.
    reset();
    void (async () => {
      try {
        const session = await drbfmService.getSession(requested);
        if (sessionAutoLoadRef.current !== requested) return;
        dispatch(setCurrentSession(session));
        if (session.status === 'completed') {
          const results = await drbfmService.getSessionResults(requested);
          if (sessionAutoLoadRef.current !== requested) return;
          dispatch(setAnalysisResults(results));
          dispatch(setWizardStep('results'));
        } else if (session.status === 'draft') {
          dispatch(setWizardStep('input'));
        }
      } catch (err) {
        if (sessionAutoLoadRef.current !== requested) return;
        // Clear stale state from the previous successfully-loaded session
        // so the user doesn't keep seeing/exporting old results under a
        // URL whose target couldn't be fetched.
        reset();
        dispatch(setAnalysisError(
          err instanceof Error ? err.message : t('drbfm.errors.sessionLoadFailed'),
        ));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the fetch-error message; excluded so a language change doesn't reset() the in-progress wizard (PR #790)
  }, [isCaseMode, sessionIdParam, dispatch, reset]);

  // Define wizard steps
  const steps: WizardStepConfig[] = [
    { id: 0, key: 'input', labelKey: 'drbfm.steps.input' },
    { id: 1, key: 'processing', labelKey: 'drbfm.steps.processing' },
    { id: 2, key: 'results', labelKey: 'drbfm.steps.results' },
  ];

  // Map wizard step to step index
  const getStepIndex = (step: string): number => {
    switch (step) {
      case 'input':
        return 0;
      case 'processing':
        return 1;
      case 'results':
        return 2;
      default:
        return 0;
    }
  };

  const activeStep = getStepIndex(wizardStep);

  // Handle back navigation
  const handleBackClick = () => {
    navigate('/');
  };

  const handleCaseModeClick = () => {
    navigate(isCaseMode ? '/drbfm' : '/drbfm?mode=case');
  };

  // Handle start analysis
  const handleStartAnalysis = async () => {
    await createSessionAndAnalyze();
  };

  // Handle cancel analysis
  const handleCancelAnalysis = () => {
    cancelAnalysis();
    goToStep('input');
  };

  // Handle new analysis
  const handleNewAnalysis = () => {
    reset();
    // Drop the historical session param so a refresh after this point
    // doesn't pull the user back into the previous session.
    if (sessionIdParam) {
      sessionAutoLoadRef.current = null;
      navigate('/drbfm', { replace: true });
    }
  };

  // Handle export
  const handleExport = async () => {
    await exportHTML();
  };

  const handleLinkCad = async (
    cadModelId: string,
    predefinedPartLabels?: PredefinedPartLabel[],
  ) => {
    const sessionId = analysisResults?.session?.id;
    if (!sessionId) return;

    setIsLinkingCad(true);
    setLinkCadError(null);
    try {
      const response = await drbfmService.linkCad(sessionId, { cadModelId, predefinedPartLabels });
      const refreshed = await drbfmService.getSessionResults(sessionId);
      dispatch(setAnalysisResults(refreshed));
      if (response.unresolvedFailureModeIds.length > 0) {
        setLinkCadError(
          t('drbfm.cadLink.partialResolved', {
            linked: response.linkedCount,
            unresolved: response.unresolvedFailureModeIds.length,
          }),
        );
      }
    } catch (err) {
      setLinkCadError(err instanceof Error ? err.message : t('drbfm.errors.cadLinkFailed'));
    } finally {
      setIsLinkingCad(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (wizardStep) {
      case 'input':
        return (
          <DRBFMInputStep
            inputData={inputData}
            onInputChange={updateInput}
            onStartAnalysis={handleStartAnalysis}
            canStart={canStartAnalysis}
            isLoading={isLoading}
            cadEnabled={cadEnabled}
          />
        );

      case 'processing':
        return (
          <DRBFMProcessingStep
            progress={analysisProgress}
            onCancel={handleCancelAnalysis}
          />
        );

      case 'results':
        return (
          <DRBFMResultsStep
            results={analysisResults}
            onExport={handleExport}
            onNewAnalysis={handleNewAnalysis}
            isExporting={isExporting}
            exportError={exportError}
            drbfmSessionId={analysisResults?.session?.id}
            drbfmItemName={analysisResults?.session?.itemName}
            onLinkCad={handleLinkCad}
            isLinkingCad={isLinkingCad}
            linkCadError={linkCadError}
            cadEnabled={cadEnabled}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <IconButton onClick={handleBackClick} size="large">
            <ArrowBackIcon />
          </IconButton>
          <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            {t('drbfm.title')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant={isCaseMode ? 'contained' : 'outlined'}
            onClick={handleCaseModeClick}
          >
            {isCaseMode ? t('drbfm.case.normalMode') : t('drbfm.case.startCase')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate('/drbfm/history')}
          >
            {t('drbfm.history.title', '分析履歴')}
          </Button>
        </Stack>
      </Box>

      {/* Error Alert */}
      {!isCaseMode && error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {isCaseMode ? (
        <Paper sx={{ p: 3, mb: 3, minHeight: 400 }}>
          <DRBFMCaseWorkflow cadEnabled={cadEnabled} />
        </Paper>
      ) : (
        <>
          {/* Stepper */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((step) => (
                <Step key={step.id}>
                  <StepLabel>{t(step.labelKey)}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          {/* Step Content */}
          <Paper sx={{ p: 3, mb: 3, minHeight: 400 }}>
            {renderStepContent()}
          </Paper>

          {/* Loading Overlay */}
          {isLoading && wizardStep === 'input' && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1300,
              }}
            >
              <Paper sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress />
                <Typography>{t('drbfm.processing.creatingSession')}</Typography>
              </Paper>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default DRBFMPage;
